import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  IconButton,
  Slider,
  TextField,
  Typography,
  Tooltip,
  MenuItem,
} from '@mui/material';
import { HelpOutline } from '@mui/icons-material';
import { api } from '../../api/apiService';
import { formatBackendErrors } from '../../utils/errorUtils';
import { countries, languages } from '../../constants';
import { EmbeddedFormSaveButton } from '../forms/EmbeddedFormSaveButton';

interface AnalyticProfileFormProps {
  profileId?: string;
  mode: 'create' | 'edit' | 'view';
  onClose?: () => void;
  embedded?: boolean;
  onSuccess?: () => void;
  /** Drawer title for view/edit: "{name} - profile details" */
  onHeaderDisplayNameChange?: (title: string | null) => void;
}

const MAX_KEYWORDS = 20;
const MAX_COUNTRIES = 5;

const AnalyticProfileForm: React.FC<AnalyticProfileFormProps> = ({
  mode,
  profileId,
  onClose,
  embedded = false,
  onSuccess,
  onHeaderDisplayNameChange,
}) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetPublic, setTargetPublic] = useState('');
  const [keywords, setKeywords] = useState<string[]>([]);
  const [keywordInput, setKeywordInput] = useState('');
  const [timeWindow, setTimeWindow] = useState<number>(24);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [isNotEditable, setIsNotEditable] = useState(false);

  useEffect(() => {
    if ((mode === 'edit' || mode === 'view') && profileId) {
      setIsFetching(true);
      setIsNotEditable(mode === 'view');
      api
        .get(`/api/analytic-profiles/${profileId}`)
        .then((response) => {
          const { name: n, description: d, keywords: kw } = response;
          setName(n);
          setDescription(d);
          setKeywords(kw);
          setTargetPublic(response.target_public);
          setTimeWindow(response.time_window_hours);
          setSelectedCountries(response.selected_countries || []);
          setSelectedLanguage(response.selected_language || '');
        })
        .catch((err) => setError(formatBackendErrors(err)))
        .finally(() => setIsFetching(false));
    }
  }, [mode, profileId]);

  useEffect(() => {
    if (!onHeaderDisplayNameChange) return;
    if (mode === 'create') {
      onHeaderDisplayNameChange(null);
      return;
    }
    const t = name.trim();
    onHeaderDisplayNameChange(t ? `${t} - profile details` : null);
  }, [mode, name, onHeaderDisplayNameChange]);

  const clearFields = () => {
    setName('');
    setDescription('');
    setTargetPublic('');
    setKeywords([]);
    setTimeWindow(24);
    setSelectedCountries([]);
    setSelectedLanguage('');
    setKeywordInput('');
    setError(null);
  };

  const resetForm = () => {
    clearFields();
    onClose?.();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);

    if (keywords.length === 0) {
      setError('At least one keyword is required.');
      setIsLoading(false);
      return;
    }

    const payload = {
      name: name,
      description: description || '',
      keywords,
      target_public: targetPublic,
      time_window_hours: timeWindow,
      selected_countries: selectedCountries,
      selected_language: selectedLanguage,
    };

    const apiCall = mode === 'create' ? api.post : api.put;
    const endpoint = mode === 'create' ? '/api/analytic-profiles' : `/api/analytic-profiles/${profileId}`;

    apiCall(endpoint, payload)
      .then(() => {
        onSuccess?.();
        if (embedded) {
          onClose?.();
        } else {
          clearFields();
        }
      })
      .catch((err) => setError(formatBackendErrors(err)))
      .finally(() => setIsLoading(false));
  };

  const handleKeywordAdd = () => {
    if (keywordInput.trim() && keywords.length < MAX_KEYWORDS && !keywords.includes(keywordInput.trim())) {
      setKeywords([...keywords, keywordInput.trim()]);
    }
    setKeywordInput('');
  };

  const handleKeywordRemove = (keyword: string) => {
    setKeywords(keywords.filter((k) => k !== keyword));
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      color: '#fafafa',
      '& fieldset': { borderColor: '#3f3f46' },
      '&:hover fieldset': { borderColor: '#a1a1aa' },
      '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
    },
    '& .MuiInputLabel-root': { color: '#a1a1aa' },
    '& .MuiInputLabel-root.Mui-focused': { color: '#60a5fa' },
  };

  const outerSx = embedded
    ? {
        width: '100%',
        maxWidth: 'none',
        mx: 0,
        p: 3,
        bgcolor: '#0a0a0a',
        border: 'none',
        boxShadow: 'none',
        borderRadius: 0,
      }
    : {
        maxWidth: 600,
        mx: 'auto',
        p: 3,
        boxShadow: 3,
        borderRadius: 2,
        bgcolor: '#0a0a0a',
        border: '1px solid #27272a',
      };

  if (isFetching) {
    return (
      <Box
        sx={{
          ...outerSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 200,
          color: '#fafafa',
        }}
      >
        <Typography>Loading profile data…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={outerSx}>
      {error && (
        <Alert
          severity="error"
          sx={{
            mb: 2,
            bgcolor: '#27272a',
            color: '#f87171',
            border: '1px solid #3f3f46',
          }}
        >
          {error}
        </Alert>
      )}
      <form onSubmit={handleSubmit}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TextField
            fullWidth
            label="Name"
            variant="outlined"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            disabled={isFetching || isNotEditable}
            sx={textFieldSx}
          />
          <Tooltip title="A unique name to identify this analytic profile">
            <IconButton size="small" sx={{ ml: 1, color: '#a1a1aa' }}>
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <TextField
            fullWidth
            label="Description"
            variant="outlined"
            multiline
            rows={4}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            required
            disabled={isFetching || isNotEditable}
            sx={textFieldSx}
          />
          <Tooltip
            title={
              <>
                Describe topics and types of news you care about. Be specific about subject areas and industries.
              </>
            }
          >
            <IconButton size="small" sx={{ ml: 1, mt: 1, color: '#a1a1aa' }}>
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 2 }}>
          <TextField
            fullWidth
            label="Target public"
            variant="outlined"
            multiline
            rows={4}
            value={targetPublic}
            onChange={(e) => setTargetPublic(e.target.value)}
            required
            disabled={isFetching || isNotEditable}
            sx={textFieldSx}
          />
          <Tooltip title="Who is the intended audience for this profile?">
            <IconButton size="small" sx={{ ml: 1, mt: 1, color: '#a1a1aa' }}>
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ color: '#fafafa' }}>
            Select countries ({selectedCountries.length}/{MAX_COUNTRIES})
          </Typography>
          <Tooltip title="Prioritize news from these countries.">
            <IconButton size="small" sx={{ ml: 1, color: '#a1a1aa' }}>
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {selectedCountries.map((country, index) => (
            <Chip
              key={`${country}-${index}`}
              label={country}
              sx={{
                bgcolor: '#27272a',
                color: '#fafafa',
                border: '1px solid #3f3f46',
              }}
            />
          ))}
        </Box>

        {!isNotEditable && (
          <Autocomplete
            multiple
            options={countries}
            value={selectedCountries}
            onChange={(_event, newValue) => {
              if (newValue.length <= MAX_COUNTRIES) {
                setSelectedCountries(newValue);
              }
            }}
            renderInput={(params) => <TextField {...params} label="Countries" variant="outlined" sx={textFieldSx} />}
            sx={{
              mb: 2,
              '& .MuiAutocomplete-tag': {
                bgcolor: '#27272a',
                color: '#fafafa',
                border: '1px solid #3f3f46',
              },
            }}
            componentsProps={{
              paper: {
                sx: {
                  bgcolor: '#0a0a0a',
                  border: '1px solid #27272a',
                  '& .MuiAutocomplete-option': {
                    color: '#fafafa',
                    '&:hover': { bgcolor: '#1a1a1a' },
                    '&[aria-selected="true"]': { bgcolor: '#27272a' },
                    '&.Mui-focused': { bgcolor: '#27272a' },
                  },
                },
              },
            }}
          />
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ color: '#fafafa' }}>
            Language
          </Typography>
          <Tooltip title="Preferred content language.">
            <IconButton size="small" sx={{ ml: 1, color: '#a1a1aa' }}>
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {!isNotEditable ? (
          <TextField
            select
            fullWidth
            label="Language"
            value={selectedLanguage}
            onChange={(e) => setSelectedLanguage(e.target.value)}
            variant="outlined"
            sx={{
              mb: 2,
              ...textFieldSx,
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    bgcolor: '#0a0a0a',
                    border: '1px solid #27272a',
                    '& .MuiMenuItem-root': {
                      color: '#fafafa',
                      '&:hover': { bgcolor: '#1a1a1a' },
                      '&.Mui-selected': { bgcolor: '#27272a' },
                      '&.Mui-selected:hover': { bgcolor: '#3f3f46' },
                    },
                  },
                },
              },
            }}
          >
            {languages.map((language) => (
              <MenuItem key={language} value={language}>
                {language}
              </MenuItem>
            ))}
          </TextField>
        ) : (
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Language"
              value={selectedLanguage}
              variant="outlined"
              disabled
              sx={textFieldSx}
            />
          </Box>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ color: '#fafafa' }}>
            Time window: {timeWindow} hours
          </Typography>
          <Tooltip title="How far back to look for relevant news.">
            <IconButton size="small" sx={{ ml: 1, color: '#a1a1aa' }}>
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Slider
          value={timeWindow}
          onChange={(_e, newValue) => setTimeWindow(newValue as number)}
          min={1}
          max={168}
          step={1}
          marks={[
            { value: 24, label: '1 day' },
            { value: 72, label: '3 days' },
            { value: 168, label: '7 days' },
          ]}
          sx={{
            mb: 3,
            color: '#60a5fa',
            '& .MuiSlider-markLabel': { color: '#a1a1aa' },
            '& .MuiSlider-thumb': {
              bgcolor: '#60a5fa',
              '&:hover': { boxShadow: '0 0 0 8px rgba(96, 165, 250, 0.16)' },
            },
            '& .MuiSlider-track': { bgcolor: '#60a5fa' },
            '& .MuiSlider-rail': { bgcolor: '#3f3f46' },
          }}
          disabled={isNotEditable}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography variant="subtitle1" sx={{ color: '#fafafa' }}>
            Keywords ({keywords.length}/{MAX_KEYWORDS})
          </Typography>
          <Tooltip title="Keywords and phrases to target content.">
            <IconButton size="small" sx={{ ml: 1, color: '#a1a1aa' }}>
              <HelpOutline fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
          {keywords.map((keyword, index) =>
            isNotEditable ? (
              <Chip
                key={`${keyword}-${index}`}
                label={keyword}
                sx={{
                  bgcolor: '#27272a',
                  color: '#fafafa',
                  border: '1px solid #3f3f46',
                }}
              />
            ) : (
              <Chip
                key={`${keyword}-${index}`}
                label={keyword}
                onDelete={() => handleKeywordRemove(keyword)}
                sx={{
                  bgcolor: '#27272a',
                  color: '#fafafa',
                  border: '1px solid #3f3f46',
                  '& .MuiChip-deleteIcon': {
                    color: '#a1a1aa',
                    '&:hover': { color: '#f87171' },
                  },
                }}
              />
            )
          )}
        </Box>
        {!isNotEditable && (
          <TextField
            fullWidth
            placeholder="Type a keyword and press Enter"
            variant="outlined"
            value={keywordInput}
            onChange={(e) => setKeywordInput(e.target.value)}
            onKeyDown={(e) => {
              if ((e.key === 'Enter' || e.key === ',') && keywords.length < MAX_KEYWORDS) {
                e.preventDefault();
                handleKeywordAdd();
              }
            }}
            sx={{
              mt: 2,
              ...textFieldSx,
            }}
          />
        )}

        {!isNotEditable &&
          (embedded ? (
            <EmbeddedFormSaveButton
              loading={isLoading}
              idleTooltip={mode === 'create' ? 'Create profile' : 'Save changes'}
              loadingTooltip={mode === 'create' ? 'Creating…' : 'Saving…'}
            />
          ) : (
            <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 1, alignItems: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                disabled={isLoading}
                sx={{
                  bgcolor: '#60a5fa',
                  color: '#0a0a0a',
                  '&:hover': { bgcolor: '#3b82f6' },
                  '&.Mui-disabled': { bgcolor: '#27272a', color: '#52525b' },
                }}
              >
                {isLoading ? (mode === 'create' ? 'Creating…' : 'Saving…') : mode === 'create' ? 'Create profile' : 'Save changes'}
              </Button>
              <Button
                variant="outlined"
                onClick={() => resetForm()}
                sx={{
                  color: '#a1a1aa',
                  borderColor: '#3f3f46',
                  '&:hover': { borderColor: '#a1a1aa', bgcolor: '#1a1a1a' },
                }}
              >
                Cancel
              </Button>
            </Box>
          ))}
      </form>
    </Box>
  );
};

export default AnalyticProfileForm;
